import { HStack, VStack, Spinner, Center, useDisclosure, Toast, useToast } from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { getHistory } from "../../../../../../utils/api/history.api";
import { useUserInfo } from "../../../../../../utils/hooks/useUserInfo";
import SlideRange from "../../../../../common/SlideRange";
import Export from "../Export";
import NoItem from "../NoItem";
import SingleAppointmentCard from "./SingleAppointmentCard";
import {
  useHistoryDispatch,
  useHistoryState,
} from "../../../../../../utils/context/HistoryContext";
import { CreateAppointment } from "../../../../../MeetingCalendar/CreateAppointment";
import { deleteAppointment, getAppointmentDetail } from "../../../../../../utils/api/meeting.api";
import { extractTime } from "../../../../../../utils";
import Popup from "../../../../../Charts/Popup";
import AppointmentReasonModal from "../../../../../MeetingCalendar/AppointmentReasonModal";
import { getPermissions } from "../../../../../../utils/api/usersetting.api";

type AppointmentType = {
  clickedRecord: any;
  setClickedRecord: Dispatch<SetStateAction<any>>;
  clickedRow: any;
  refetchHistory?: any;
};

function Appointment({ clickedRecord, setClickedRecord, clickedRow, refetchHistory }: AppointmentType) {
  const toast = useToast();
  const dealership = useUserInfo("dealership");
  const user = useUserInfo("user");
  const [appointmentActivities, setAppointmentActivities] = useState<any[]>([]);
  const [sliderValue, setSliderValue] = useState("30");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const { shouldRefetch } = useHistoryState();
  const dispatch = useHistoryDispatch();
  const {
    isOpen: isOpenAppointment,
    onOpen: onOpenAppointment,
    onClose: onCloseAppointment,
  } = useDisclosure();

  const { data: appointmentDetail }: any = useQuery({
    queryKey: [
      "Appointment-detail",
      dealership?.id,
      null,
      clickedRecord?.details?.id,
      null,
    ],
    queryFn: getAppointmentDetail,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!clickedRecord?.details?.id,
  });

  const { data: userObj } = useQuery({
    queryKey: ["userById", user?.id, user?.dealership?.id],
    queryFn: getPermissions,
    enabled: !!user?.id,
  });  

  const isAppointmentUpdateAllowed = userObj?.data?.permissions.some(
    (permission: any) => permission.module_key === "appointment_edit_update" && permission.allowed
  );



  const modifiedData = useMemo(() => {
    if (!appointmentDetail) return {};
  
    return {
      ...appointmentDetail?.data,
      date: appointmentDetail?.data?.start_time?.substring(0, 10),
      start_time: appointmentDetail?.data?.start_time,
      type: { value: appointmentDetail?.data?.event_type },
      userid: appointmentDetail?.data?.participants?.map((user: any) => ({
        value: user.id,
        label: user.full_name,
      })),
    };
  }, [appointmentDetail]);

  const deletemutation = useMutation(deleteAppointment);

  const handleDelete = () => {
    deletemutation.mutate(
      {
        id: dealership.id,
        meetingId: clickedRecord?.details?.id,
        contactid: clickedRow?.id,
        reason: deleteReason,
      },
      {
        onSuccess(data, variables, context) {
          setIsDeleteModalOpen(false);
          setAppointmentActivities([]);
          refetch()
          refetchHistory();
          toast({
            description: "Event deleted successfully.",
            status: "success",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
        },
      }
    );
  };

  const { isLoading, isFetching, refetch } = useQuery({
    queryKey: ["appoitmrnt", clickedRow?.id, sliderValue, page],
    queryFn: () =>
      getHistory({
        dealershipId: dealership?.id,
        contactId: clickedRow?.id,
        sub_type: "appointment",
        days: sliderValue,
        page,
      }),
    retry: false,
    onSuccess: (data) => {
      if (appointmentActivities[0]?.contact_id !== clickedRow?.id) {
        setAppointmentActivities(data.data.histories || []);
        setPage(1);
      } else {
        const filteredNewHistories = data?.data?.histories?.filter(
          (newHistory: any) =>
            !appointmentActivities?.some(
              (existing) => existing.id === newHistory.id
            )
        );

        setAppointmentActivities((prev) => [...prev, ...filteredNewHistories]);
      }
      setTotalCount(data.data.histories_count);
    },
  });

  useEffect(() => {
    setPage(1);
    setAppointmentActivities([]);
    refetch();
  }, [clickedRow?.id, refetch, sliderValue]);

  useEffect(() => {
    if (shouldRefetch) {
      setPage(1);
      setAppointmentActivities([]);
      refetch();
    }

    dispatch({ type: "SET_SHOULD_REFETCH", payload: { shouldRefetch: false } });
  }, [dispatch, refetch, shouldRefetch]);

  return (
    <>
      {" "}
      <Export sub_type="appointment" days={sliderValue} id={clickedRow?.id} type="AppointmentHistory"/>
      <HStack background="var(--grey-200)" padding="1rem" w="100%">
        <SlideRange handleClick={(value) => setSliderValue(value)} />
      </HStack>{" "}
      {(isFetching&&page===1) && (
        <Center w="100%" h="calc(100vh - 375px)">
          <Spinner />
        </Center>
      )}{" "}
      {!!appointmentActivities.length &&
      !!appointmentActivities.filter((item) => !!item.details).length ? (
        <div
          id="scrollableDivAppointment"
          style={{ height: "calc(100vh - 375px)", overflow: "auto" }}
        >
          <InfiniteScroll
            dataLength={appointmentActivities.length}
            next={() => {
              setPage((prev) => prev + 1);
            }}
            hasMore={appointmentActivities.length < totalCount}
            loader={<h4>Loading...</h4>}
            scrollableTarget="scrollableDivAppointment"
          >
            <VStack
              alignItems="flex-start"
              gap="1rem"
              padding="1rem"
              background="var(--grey-200)"
            >
              {appointmentActivities.map((item) => {
                return (
                  <SingleAppointmentCard
                    item={item}
                    setClickedRecord={setClickedRecord}
                    openAppointmentDrawer={onOpenAppointment}
                    onDeleteClick={() => {
                      setDeleteReason("");
                      setIsDeleteModalOpen(true);
                    }}
                    isEditable={true}
                    isAppointmentUpdateAllowed={isAppointmentUpdateAllowed}
                  />
                );
              })}
            </VStack>
          </InfiniteScroll>
          {
            isOpenAppointment &&
            <CreateAppointment
              onClose={() => {
                onCloseAppointment();
              }}
              isOpen={isOpenAppointment}
              contactid={clickedRow?.id}
              slotData={modifiedData}
              editMode={true}
              apiCall={() => {
                setAppointmentActivities([]);
                refetch(); 
                refetchHistory();
              }}
              isHistoryAppointment={true}
            />
          }
          {isDeleteModalOpen && (
            <AppointmentReasonModal
              isOpen={isDeleteModalOpen}
              headerText="Reason for Cancellation"
              bodyText="Please provide a reason for cancelling this appointment:"
              setModalOpen={setIsDeleteModalOpen}
              onSubmit={handleDelete}
              reason={deleteReason}
              setReason={setDeleteReason}
            />
          )}
        </div>
      ) : (
        !isLoading && !isFetching && <NoItem />
      )}
    </>
  );
}

export default Appointment;
